import { isEmpty } from 'lodash';
import { Space, Typography } from 'antd';
import { SegmentedValue } from 'antd/lib/segmented';
import { setNestedObjectValues, useFormik } from 'formik';
import { FileTextOutlined, SaveOutlined } from '@ant-design/icons';
import { HTMLAttributes, ReactElement, useEffect, useMemo, useState } from 'react';
import { useRequestPromise } from 'hooks/useRequest';
import getAllDeclarationTemplateNames from 'utils/requests/getAllDeclarationTemplateNames';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import { useDeclarationContext } from 'views/declarations/common/DeclarationContext';
import { getDeclarationTitle } from 'utils/declarationTitle';
import { StyledButton } from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import styles from 'views/declarations/declaration-tabs/declaration-form/DeclarationTabView.module.css';
import Box44, { Box44Context } from 'views/declarations/common/box44/Box44';
import { box44PathAndFieldNames } from 'views/declarations/common/box44/mappers';
import { MessageTypes } from 'store/declarations/enums/common/declaration-types';
import Segmented from 'components/new-ui/base/segmented/Segmented';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import { TemplateTitle } from './styles';
import { getValidationSchema } from './validationSchema';
import { TemplateFormType, useTemplateContext } from './TemplateContext';
import DeclarationInput from '../declarations/formInput/DeclarationInput';

interface Form {
    templateName?: string | null;
}

const DEFAULT_FORM_OPTIONS: { label: string; value: TemplateFormType }[] = [
    { label: 'Master Information', value: 'master' },
    { label: 'Item Details', value: 'product' },
];

interface Props extends HTMLAttributes<HTMLDivElement> {
    active?: TemplateFormType;
    creating?: boolean;
    formOptions?: { label: string; value: TemplateFormType }[];
    onChangeActiveForm?: (form: TemplateFormType) => void;
    onSave?: (templateName?: string | null) => void;
    onClose?: () => void;
    setEditedTemplateName?: (editedTemplateName: string | null) => void;
}

const TemplateHeader = (props: Props): ReactElement => {
    const { options, isViewOnly } = useTemplateContext();
    const { clearCards } = useDeclarationContext();
    const { data: templateNames } = useRequestPromise(() =>
        getAllDeclarationTemplateNames({ templateData: { ...options } })
    );

    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isBox44ModalOpen, setIsBox44ModalOpen] = useState(false);

    const box44Fields = useMemo(() => {
        if (!options?.formType) return undefined;
        if (options.country === 'EU') return undefined;
        return box44PathAndFieldNames[options.formType];
    }, [options?.formType, options?.country]);

    const formik = useFormik<Form>({
        initialValues: { templateName: options?.name },
        enableReinitialize: true,
        validateOnChange: true,
        validateOnBlur: true,
        validateOnMount: true,
        validationSchema: getValidationSchema(templateNames, options?.name),
        onSubmit: () => {},
    });

    const handleSave = async () => {
        const errors = await formik.validateForm();
        if (!isEmpty(errors)) {
            formik.setTouched(setNestedObjectValues(errors, true));
            return;
        }
        setIsSaveModalOpen(true);
    };

    const handleClose = () => {
        props.onClose?.();
    };

    useEffect(() => {
        if (!formik.values.templateName) return;

        props.setEditedTemplateName?.(formik.values.templateName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.templateName]);

    return (
        <>
            <div
                className={styles.main}
                style={{
                    marginBottom: '16px',
                }}
            >
                <div className={styles.infoRow}>
                    <TemplateTitle className={`${styles.infoHeader} ${styles.indicator}`}>
                        Form Template Builder
                    </TemplateTitle>

                    <span
                        className={styles.indicator}
                        style={{
                            verticalAlign: 'bottom',
                        }}
                    >
                        <FileTextOutlined />{' '}
                        {getDeclarationTitle(
                            options?.country as any,
                            options?.declarationType as any,
                            options?.formType as any
                        )}
                    </span>
                </div>
                <div
                    className={styles.actions}
                    style={{
                        alignItems: 'center',
                    }}
                >
                    <StyledButton onClick={() => handleClose()}>Cancel</StyledButton>
                    {!isViewOnly && (
                        <StyledButton type="primary" onClick={handleSave}>
                            {props.creating ? 'Save' : 'Save Changes'}
                        </StyledButton>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 32 }}>
                <div>
                    <Segmented
                        options={props.formOptions ?? DEFAULT_FORM_OPTIONS}
                        value={props.active}
                        onChange={(value: SegmentedValue) => {
                            clearCards?.();
                            props.onChangeActiveForm?.(value as TemplateFormType);
                        }}
                    />
                </div>
                <Space size={8} style={{ width: '100%', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <LabelRequiredSymbol />
                    <label>Template Name : </label>
                    <div style={{ minWidth: 400 }}>
                        <DeclarationInput
                            required
                            normal
                            condensed
                            disabled={isViewOnly}
                            style={{ maxWidth: '100%' }}
                            placeholder="Enter template name"
                            {...getFormikProps('templateName', formik)}
                        />
                    </div>

                    {box44Fields && (
                        <Box44Context.Provider
                            value={{
                                path:
                                    props.active === 'product'
                                        ? `product.defaults.${box44Fields.productPath}`
                                        : `master.defaults.${box44Fields.masterPath}`,
                                documentTypeName: box44Fields.documentTypeName,
                                documentIdentifierName: box44Fields.documentIdentifierName,
                                monetaryAmountName: box44Fields.monetaryAmountName,
                                measurementName: box44Fields.measurementName,
                                hasAdditionalInfo:
                                    props.active === 'product' &&
                                    options?.declarationType === 'IMPORT' &&
                                    options?.country === 'IRELAND',
                                messageType: options?.formType as MessageTypes,
                                isProduct: props.active === 'product',
                            }}
                        >
                            <Box44.Button onClick={() => setIsBox44ModalOpen(true)} />
                            <Box44.Modal
                                viewOnly={isViewOnly}
                                open={isBox44ModalOpen}
                                onCancel={() => setIsBox44ModalOpen(false)}
                            />
                        </Box44Context.Provider>
                    )}
                </Space>

                <ConfirmationModal
                    open={isSaveModalOpen}
                    title="Save Changes?"
                    icon={<SaveOutlined />}
                    width={500}
                    cancelButtonText="Cancel"
                    confirmButtonText="Save Changes"
                    onClose={() => setIsSaveModalOpen(false)}
                    onCancel={() => setIsSaveModalOpen(false)}
                    onConfirm={() => props.onSave?.(formik.values.templateName)}
                    content={
                        <>
                            <Typography>Are you sure you want to save changes? </Typography>
                        </>
                    }
                />
            </div>
        </>
    );
};

export default TemplateHeader;
